import {
    useEffect,
    useLayoutEffect,
    useRef,
    useImperativeHandle,
    forwardRef,
    useCallback,
} from 'react';
import OpenSeadragon from 'openseadragon';
import OpenSeadragonViewer from './OpenSeadragon';
import * as annotationService from '../api/annotationService';
import { dsaToAnnotationToolkit, annotationToolkitToDSA } from '../utils/annotationAdapter';
import '../submodules/osd-paperjs-annotation/src/css/annotationui.css';

/**
 * WSI viewer with osd-paperjs-annotation: composes OpenSeadragonViewer and attaches
 * AnnotationToolkit (toolbar, layer UI, layout). Loads/saves annotations from DSA backend
 * when itemId (girderItemId) is provided. Exposes save() via ref for the parent to call.
 * Requires submodule dist to be built. Sets window.annotationToolkit for console debugging.
 *
 * Props:
 *   toolkitOptions  - optional object merged into the AnnotationToolkit constructor options.
 *     Use `toolbar: { tools: ['select', 'ruler'] }` to restrict which tools are available.
 *     Defaults: toolbar: true, layerUI: true, layout: true.
 *   onToolkitReady  - optional (toolkit) => void callback, called once the AnnotationToolkit
 *     instance has been created. Useful for wiring the toolkit into external hooks.
 *     After the dynamic import() above this path, callbacks and options are read from refs
 *     so callers always receive the latest closure (avoids stale props across await).
 */
const OpenSeadragonAnnotate = forwardRef(function OpenSeadragonAnnotate(
    { data, girderItemId, tileMetadata, itemId, style, toolkitOptions, onToolkitReady },
    ref
) {
    const toolkitRef = useRef(null);
    const currentAnnotationIdRef = useRef(null);
    const cancelledRef = useRef(false);
    const prevOSDRef = useRef(undefined);
    const id = girderItemId || itemId || (data && data._id) || null;

    const onToolkitReadyRef = useRef(onToolkitReady);
    const toolkitOptionsRef = useRef(toolkitOptions);
    const idRef = useRef(id);

    useLayoutEffect(() => {
        onToolkitReadyRef.current = onToolkitReady;
        toolkitOptionsRef.current = toolkitOptions;
        idRef.current = id;
    });

    const initAnnotation = useCallback(async (viewer) => {
        const mod = await import(
            /* webpackChunkName: "osd-annotation" */ '../submodules/osd-paperjs-annotation/dist/main.js'
        );
        if (cancelledRef.current) return;
        const AnnotationToolkit =
            mod.AnnotationToolkit || mod.default?.AnnotationToolkit || (typeof window !== 'undefined' && window.AnnotationToolkit);
        if (!AnnotationToolkit) {
            console.error('OpenSeadragonAnnotate: AnnotationToolkit not found in osd-paperjs-annotation dist');
            return;
        }
        const attachToolkit = () => {
            if (cancelledRef.current || !viewer.canvas || toolkitRef.current) return;
            const opts = toolkitOptionsRef.current;
            const readyCb = onToolkitReadyRef.current;
            // When onToolkitReady is provided, default to headless mode (no native UI)
            // so the caller can drive tools entirely through React components.
            const isHeadless = typeof readyCb === 'function';
            const tk = new AnnotationToolkit(viewer, {
                toolbar: opts?.toolbar ?? (isHeadless ? false : true),
                layerUI: opts?.layerUI ?? (isHeadless ? false : true),
                layout: opts?.layout ?? (isHeadless ? false : true),
            });
            toolkitRef.current = tk;
            window.annotationToolkit = tk;
            if (readyCb) {
                // Headless mode: add the tools the external manager will drive, then
                // hand the fully-initialised toolkit to the caller. Tools must be added
                // before onToolkitReady fires so that getTool(name) works immediately.
                const toolNames = opts?.tools ?? ['default', 'ruler'];
                tk.addTools(toolNames);
                readyCb(tk);
                return;
            }

            const currentId = idRef.current;
            if (currentId) {
                annotationService.listAnnotations(currentId)
                    .then((list) => {
                        if (list.length === 0 || cancelledRef.current) return;
                        return annotationService.getAnnotation(list[0]._id);
                    })
                    .then((doc) => {
                        if (!doc || cancelledRef.current) return;
                        const geoJSON = dsaToAnnotationToolkit(doc);
                        if (geoJSON.length > 0 && toolkitRef.current) {
                            toolkitRef.current.addFeatureCollections(geoJSON, true);
                        }
                        currentAnnotationIdRef.current = doc._id;
                    })
                    .catch((err) => {
                        console.warn('OpenSeadragonAnnotate: could not load annotations', err);
                    });
            }
        };

        if (viewer.canvas) {
            attachToolkit();
        } else {
            viewer.addHandler('open', attachToolkit);
        }
    }, [id]);

    const onViewerReady = useCallback((viewer) => {
        cancelledRef.current = false;
        prevOSDRef.current = window.OpenSeadragon;
        window.OpenSeadragon = OpenSeadragon;
        initAnnotation(viewer).catch((err) => {
            console.error('OpenSeadragonAnnotate: failed to load annotation toolkit', err);
        });
    }, [initAnnotation]);

    useEffect(() => {
        return () => {
            cancelledRef.current = true;
            const toolkit = toolkitRef.current;
            toolkitRef.current = null;
            if (toolkit) {
                try {
                    toolkit.destroy();
                } catch (e) {
                    console.warn('OpenSeadragonAnnotate: toolkit.destroy error', e);
                }
            }
            window.annotationToolkit = null;
            if (window.OpenSeadragon === OpenSeadragon && prevOSDRef.current !== undefined) {
                window.OpenSeadragon = prevOSDRef.current;
            } else if (window.OpenSeadragon === OpenSeadragon) {
                delete window.OpenSeadragon;
            }
        };
    }, [id]);

    useImperativeHandle(
        ref,
        () => ({
            async save() {
                const tk = toolkitRef.current;
                if (!tk) return { ok: false, error: 'Viewer not ready' };
                if (!id) return { ok: false, error: 'No item id' };
                try {
                    const geoJSON = tk.toGeoJSON();
                    const elements = annotationToolkitToDSA(geoJSON);
                    const body = { name: 'Default', elements };
                    const annotationId = currentAnnotationIdRef.current;
                    if (annotationId) {
                        await annotationService.updateAnnotation(annotationId, id, body);
                    } else {
                        const created = await annotationService.createAnnotation(id, body);
                        currentAnnotationIdRef.current = created._id;
                    }
                    return { ok: true };
                } catch (err) {
                    return { ok: false, error: err?.message || 'Save failed' };
                }
            }
        }),
        [id]
    );

    if (!id) return null;

    return (
        <OpenSeadragonViewer
            data={data}
            girderItemId={girderItemId}
            tileMetadata={tileMetadata}
            itemId={itemId}
            style={style}
            onViewerReady={onViewerReady}
        />
    );
});

export default OpenSeadragonAnnotate;
